/**
 * History & Milestones — ported from the v1 dashboard, which showed every
 * run a learner had ever started with its band, its state and exactly one
 * action. It was the most useful block on that page, because a learner with
 * an abandoned run could see it and finish it instead of starting over.
 *
 * Straight read of `assessments`. No derived scores, no invented labels.
 */
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export interface AssessmentRow {
  id: string;
  status: string;
  age_group: string | null;
  started_at: string | null;
  created_at: string | null;
}

/** `age_group` is a DB enum ('12-14' | '15-18' | '18+'). Map to the walk. */
export function bandParamFor(ageGroup: string | null): string {
  if (ageGroup === "12-14") return "12-14";
  if (ageGroup === "15-18") return "15-18";
  return "18+";
}

const STATE: Record<string, { label: string; tone: string; icon: typeof Clock }> = {
  in_progress: { label: "In progress", tone: "bg-celebrate/15 text-celebrate", icon: Clock },
  completed: { label: "Completed", tone: "bg-primary/15 text-primary", icon: CheckCircle2 },
  scored: { label: "Scored", tone: "bg-primary/15 text-primary", icon: Trophy },
};

function relative(iso: string | null): string {
  if (!iso) return "recently";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months <= 1 ? "about a month ago" : `${months} months ago`;
}

export default function AssessmentHistory({
  rows,
  loading,
}: {
  rows: AssessmentRow[];
  loading?: boolean;
}) {
  if (loading) {
    return (
      <section className="calm-card p-6 sm:p-8">
        <h3 className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          History &amp; milestones
        </h3>
        <div className="mt-5 space-y-3">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
        </div>
      </section>
    );
  }

  return (
    <section className="calm-card p-6 sm:p-8">
      <h3 className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        History &amp; milestones
      </h3>

      {rows.length === 0 ? (
        <div className="calm-card-quiet mt-5 p-6 text-center">
          <p className="font-display text-base font-semibold">Nothing here yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Your first discovery run will show up here, with everything you can pick back up.
          </p>
          <Button asChild className="mt-5 h-12 rounded-2xl px-6">
            <Link to="/start">Begin your discovery</Link>
          </Button>
        </div>
      ) : (
        <ul className="mt-5 space-y-3">
          {rows.map((r) => {
            const s = STATE[r.status] ?? {
              label: r.status,
              tone: "bg-muted text-muted-foreground",
              icon: Clock,
            };
            const Icon = s.icon;
            const band = bandParamFor(r.age_group);
            const resume = r.status === "in_progress";
            return (
              <li
                key={r.id}
                className="calm-card-quiet flex flex-wrap items-center gap-4 p-4 sm:p-5"
              >
                <span
                  className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${s.tone}`}
                  aria-hidden="true"
                >
                  <Icon className="h-5 w-5" />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display truncate text-sm font-semibold">
                      WeKIT™ Career Clarity 360
                    </p>
                    <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                      {r.age_group ?? "18+"}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.tone}`}>
                      {s.label}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Started {relative(r.started_at ?? r.created_at)}
                    {resume && " · pick it up to unlock your report"}
                  </p>
                </div>

                <Button
                  asChild
                  variant={resume ? "default" : "ghost"}
                  className="h-11 rounded-2xl px-5"
                >
                  <Link
                    to={
                      resume
                        ? `/assessment/v2?band=${encodeURIComponent(band)}`
                        : `/assessment/v2/result?band=${encodeURIComponent(band)}`
                    }
                  >
                    {resume ? "Resume" : "View"}
                  </Link>
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
